/**
 * Zustand store for theme management with localStorage persistence.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logger } from "../lib/logger";
import { getThemeById, getDefaultTheme, applyTheme } from "../lib/themes";
import { DEFAULT_THEME_ID } from "../types/theme";
import type { Theme } from "../types/theme";

interface ThemeStore {
  // State
  currentThemeId: string;
  customTheme: Theme | null;
  preLoginThemeId: string | null;

  // Actions
  setTheme: (themeId: string, syncToBackend?: boolean) => void;
  setCustomTheme: (theme: Theme) => void;
  clearCustomTheme: () => void;
  resetToDefault: () => void;
  getCurrentTheme: () => Theme;
  initializeTheme: () => void;
  savePreLoginTheme: () => void;
  restorePreLoginTheme: () => void;
  loadUserTheme: (themeId: string | null | undefined) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      currentThemeId: DEFAULT_THEME_ID,
      customTheme: null,
      preLoginThemeId: null,

      setTheme: async (themeId: string, syncToBackend: boolean = true) => {
        const theme = getThemeById(themeId);
        if (theme) {
          set({ currentThemeId: themeId });
          applyTheme(theme);

          // Sync to backend if user is authenticated and sync is enabled
          if (syncToBackend) {
            try {
              const { api } = await import("../lib/api");
              const { useAuthStore } = await import("./authStore");
              const isAuthenticated = useAuthStore.getState().isAuthenticated();

              if (isAuthenticated) {
                // Send theme ID and primary color to backend
                const response = await api.updateTheme(
                  themeId,
                  theme.palette.primary,
                );

                // Update auth store with new theme data (without triggering theme reload)
                useAuthStore.getState().updateUserProfile({
                  theme_preference: response.theme_preference,
                  theme_primary_color: response.theme_primary_color,
                });

                // Dispatch event to notify any connected game sessions
                window.dispatchEvent(
                  new CustomEvent("profile-updated", {
                    detail: {
                      profile_emoji:
                        useAuthStore.getState().user?.profile_emoji,
                      theme_primary_color: response.theme_primary_color,
                    },
                  }),
                );
              }
            } catch (error) {
              logger.error("theme_sync_failed", { error });
            }
          }
        }
      },

      setCustomTheme: (theme: Theme) => {
        set({ customTheme: theme, currentThemeId: theme.id });
        applyTheme(theme);
      },

      clearCustomTheme: () => {
        set({ customTheme: null });
      },

      resetToDefault: () => {
        const defaultTheme = getDefaultTheme();
        set({ currentThemeId: DEFAULT_THEME_ID, customTheme: null });
        applyTheme(defaultTheme);
      },

      getCurrentTheme: () => {
        const { currentThemeId, customTheme } = get();
        if (customTheme && customTheme.id === currentThemeId) {
          return customTheme;
        }
        return getThemeById(currentThemeId) || getDefaultTheme();
      },

      initializeTheme: () => {
        const theme = get().getCurrentTheme();
        applyTheme(theme);
      },

      savePreLoginTheme: () => {
        const { currentThemeId } = get();
        set({ preLoginThemeId: currentThemeId });
      },

      restorePreLoginTheme: () => {
        const { preLoginThemeId } = get();
        if (preLoginThemeId) {
          const theme = getThemeById(preLoginThemeId);
          if (theme) {
            set({ currentThemeId: preLoginThemeId, preLoginThemeId: null });
            applyTheme(theme);
            return;
          }
        }
        // Fall back to default theme if no pre-login theme saved
        const defaultTheme = getDefaultTheme();
        set({ currentThemeId: defaultTheme.id, preLoginThemeId: null });
        applyTheme(defaultTheme);
      },

      loadUserTheme: (themeId: string | null | undefined) => {
        if (themeId) {
          const theme = getThemeById(themeId);
          if (theme) {
            set({ currentThemeId: themeId });
            applyTheme(theme);
          }
        }
      },
    }),
    {
      name: "responsum-theme",
      partialize: (state) => ({
        currentThemeId: state.currentThemeId,
        customTheme: state.customTheme,
        preLoginThemeId: state.preLoginThemeId,
      }),
    },
  ),
);
